import { Badge, Group, Pagination, Select, Text } from '@mantine/core'
import { Table } from '@ospace/shared'
import { ColumnDef } from '@tanstack/react-table'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'

import { deduplicationKindConfig, type DeduplicationKind } from '../config'
import { useDeduplicationRunGroups } from '../hooks/useDeduplicationRunGroups'
import type { GroupRunSummary } from '../types'
import { groupDetailPath } from '../utils/paths'
import { TraceStatusBadge } from './StatusBadge'

const PAGE_SIZE = 50

const companyTerminalOptions = [
  { value: '', label: 'All terminals' },
  { value: 'MERGE', label: 'MERGE' },
  { value: 'CLEANUP', label: 'CLEANUP' },
  { value: 'FLAG_FOR_REVIEW', label: 'FLAG_FOR_REVIEW' },
  { value: 'NO_ACTION', label: 'NO_ACTION' },
  { value: 'SKIPPED', label: 'SKIPPED' },
]

const contactTerminalOptions = [
  { value: '', label: 'All terminals' },
  { value: 'MERGE', label: 'MERGE' },
  { value: 'FLAG_FOR_REVIEW', label: 'FLAG_FOR_REVIEW' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'SKIP', label: 'SKIP' },
]

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'in_progress', label: 'in_progress' },
  { value: 'done', label: 'done' },
  { value: 'failed', label: 'failed' },
  { value: 'skipped', label: 'skipped' },
]

const TerminalBadge = ({ terminal }: { terminal: string | null }) => {
  if (!terminal) {
    return (
      <Badge color='gray' variant='outline'>
        pending
      </Badge>
    )
  }
  const color =
    terminal === 'MERGE'
      ? 'violet'
      : terminal === 'CLEANUP'
        ? 'red'
        : terminal === 'FLAG_FOR_REVIEW'
          ? 'orange'
          : terminal === 'SKIPPED' || terminal === 'SKIP'
            ? 'gray'
            : 'blue'
  return (
    <Badge color={color} variant='light'>
      {terminal}
    </Badge>
  )
}

const DiscoverySourceBadge = ({ source }: { source: string | null | undefined }) => {
  if (!source) {
    return (
      <Text size='sm' c='dimmed'>
        —
      </Text>
    )
  }
  const label = source === 'linkedin' ? 'Same LinkedIn' : source === 'name' ? 'Same name' : source
  const color = source === 'linkedin' ? 'blue' : source === 'name' ? 'teal' : 'gray'
  return (
    <Badge color={color} variant='light'>
      {label}
    </Badge>
  )
}

const buildColumns = (kind: DeduplicationKind, runId: string): ColumnDef<GroupRunSummary>[] => {
  const kindConfig = deduplicationKindConfig[kind]

  return [
    {
      accessorKey: 'companyName',
      header: kindConfig.entityLabel,
      cell: (info) => (
        <Link
          to={groupDetailPath(kind, runId, info.row.original.groupTraceId)}
          className='text-dark fw-bolder text-hover-primary'
        >
          {info.getValue() as string}
        </Link>
      ),
    },
    {
      accessorKey: 'companyCount',
      header: kindConfig.entityCountLabel,
      size: 80,
    },
    ...(kind === 'contact'
      ? [
          {
            accessorKey: 'discoverySource',
            header: 'Discovery',
            cell: (info) => (
              <DiscoverySourceBadge source={info.getValue() as string | null | undefined} />
            ),
          } satisfies ColumnDef<GroupRunSummary>,
        ]
      : []),
    {
      accessorKey: 'terminal',
      header: 'Decision',
      cell: (info) => <TerminalBadge terminal={info.getValue() as string | null} />,
    },
    ...(kind !== 'contact'
      ? [
          {
            id: 'gates',
            header: 'Gates',
            cell: (info) => {
              const gates = info.row.original.gates ?? []
              return gates.length > 0 ? gates.join(', ') : '—'
            },
          } satisfies ColumnDef<GroupRunSummary>,
        ]
      : []),
    {
      accessorKey: 'status',
      header: 'Status',
      cell: (info) => {
        const raw = info.getValue() as string
        const status = raw === 'failed' ? 'failed' : raw === 'in_progress' ? 'in_progress' : 'done'
        return <TraceStatusBadge status={status} />
      },
    },
    {
      accessorKey: 'summary',
      header: 'Summary',
      cell: (info) => (
        <Text size='sm' lineClamp={2} style={{ maxWidth: 280 }}>
          {(info.getValue() as string | null) ?? '—'}
        </Text>
      ),
    },
  ]
}

type Props = {
  kind: DeduplicationKind
  runId: string
}

export const RunGroupsTable = ({ kind, runId }: Props) => {
  const [terminalFilter, setTerminalFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [offset, setOffset] = useState(0)

  const { data, isFetching, isLoading } = useDeduplicationRunGroups(runId, {
    limit: PAGE_SIZE,
    offset,
    terminal: terminalFilter || undefined,
    status: statusFilter || undefined,
    q: searchQuery || undefined,
  })

  const totalPages = data ? Math.ceil(data.total / PAGE_SIZE) : 0
  const terminalOptions = kind === 'contact' ? contactTerminalOptions : companyTerminalOptions

  useEffect(() => {
    setOffset(0)
  }, [searchQuery])

  return (
    <div className='card'>
      <div className='card-header border-0 pt-5'>
        <Group justify='space-between' wrap='wrap'>
          <h3 className='card-title mb-0'>
            <span className='card-label fw-bolder fs-3'>Groups</span>
          </h3>
          <Text size='sm' c='dimmed'>
            {data?.total ?? 0} groups
          </Text>
        </Group>
      </div>
      <div className='card-body py-3'>
        <Group mb='md' grow>
          <Select
            label='Decision'
            data={terminalOptions}
            value={terminalFilter}
            onChange={(value) => {
              setTerminalFilter(value ?? '')
              setOffset(0)
            }}
          />
          <Select
            label='Status'
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value ?? '')
              setOffset(0)
            }}
          />
        </Group>

        <Table
          columns={buildColumns(kind, runId)}
          data={data?.results ?? []}
          isLoading={isLoading}
          isFetching={isFetching}
          hidePagination
          setServerSideFilter={setSearchQuery}
        />

        {totalPages > 1 && (
          <Group justify='center' mt='md'>
            <Pagination
              total={totalPages}
              value={Math.floor(offset / PAGE_SIZE) + 1}
              onChange={(page) => setOffset((page - 1) * PAGE_SIZE)}
            />
          </Group>
        )}
      </div>
    </div>
  )
}
